import React from 'react';
import { FiPlus, FiChevronLeft, FiChevronRight, FiX, FiClock } from 'react-icons/fi';

const Calendar = ({
  currentMonth,
  calendarEntries,
  clients,
  newEvent,
  showEventModal,
  eventSaving,
  setShowEventModal,
  setNewEvent,
  handlePreviousMonth,
  handleNextMonth,
  handleDateClick,
  handleCreateEvent
}) => {
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getEventsForDate = (date) => {
    return calendarEntries.filter(entry => {
      const entryDate = new Date(entry.date);
      return (
        entryDate.getFullYear() === date.getFullYear() &&
        entryDate.getMonth() === date.getMonth() &&
        entryDate.getDate() === date.getDate()
      );
    });
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Generate calendar days
  const calendarDays = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }

  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <div className="calendar-section-modern">
      {/* Calendar Header */}
      <div className="calendar-header-modern">
        <div className="header-left">
          <h2>Training Calendar</h2>
          <p className="calendar-subtitle">{calendarEntries.length} event{calendarEntries.length !== 1 ? 's' : ''} scheduled</p>
        </div>
        <button className="add-event-btn" onClick={() => setShowEventModal(true)}>
          <FiPlus />
          Add Event
        </button>
      </div>

      {/* Month Navigation */}
      <div className="month-navigation">
        <button className="nav-btn" onClick={handlePreviousMonth}>
          <FiChevronLeft />
        </button>
        <h3 className="month-year">{monthNames[month]} {year}</h3>
        <button className="nav-btn" onClick={handleNextMonth}>
          <FiChevronRight />
        </button>
      </div>

      {/* Day Names */}
      <div className="calendar-days-header">
        {dayNames.map(day => (
          <div key={day} className="day-name">{day}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid-modern">
        {calendarDays.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="calendar-day empty"></div>;
          }

          const date = new Date(year, month, day);
          const events = getEventsForDate(date);
          const isToday =
            date.getDate() === new Date().getDate() &&
            date.getMonth() === new Date().getMonth() &&
            date.getFullYear() === new Date().getFullYear();

          return (
            <div
              key={day}
              className={`calendar-day ${isToday ? 'today' : ''} ${events.length > 0 ? 'has-events' : ''}`}
              onClick={() => handleDateClick(date)}
            >
              <div className="day-number">{day}</div>
              {events.length > 0 && (
                <div className="day-events">
                  {events.slice(0, 2).map(event => (
                    <div key={event.id} className={`event-dot event-${event.type}`} title={event.title}>
                      <FiClock className="event-icon" />
                      <span className="event-time">{event.startTime}</span>
                      <span className="event-title">{event.title}</span>
                    </div>
                  ))}
                  {events.length > 2 && (
                    <div className="more-events">+{events.length - 2} more</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Event Creation Modal */}
      {showEventModal && (
        <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Calendar Event</h3>
              <button className="modal-close" onClick={() => setShowEventModal(false)}>
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="eventTitle">Event Title *</label>
                <input
                  type="text"
                  id="eventTitle"
                  placeholder="e.g., Training Session with John"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="modern-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="eventType">Event Type *</label>
                <select
                  id="eventType"
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}
                  className="modern-select"
                >
                  <option value="session">Training Session</option>
                  <option value="check-in">Client Check-in</option>
                  <option value="meal-plan-review">Meal Plan Review</option>
                  <option value="progress-check">Progress Check</option>
                  <option value="consultation">Consultation</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="eventDate">Date *</label>
                  <input
                    type="date"
                    id="eventDate"
                    value={newEvent.date.toISOString().split('T')[0]}
                    onChange={(e) => setNewEvent({ ...newEvent, date: new Date(e.target.value) })}
                    className="modern-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="startTime">Start Time *</label>
                  <input
                    type="time"
                    id="startTime"
                    value={newEvent.startTime}
                    onChange={(e) => setNewEvent({ ...newEvent, startTime: e.target.value })}
                    className="modern-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="endTime">End Time *</label>
                  <input
                    type="time"
                    id="endTime"
                    value={newEvent.endTime}
                    onChange={(e) => setNewEvent({ ...newEvent, endTime: e.target.value })}
                    className="modern-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="clientSelect">Link to Client (Optional)</label>
                <select
                  id="clientSelect"
                  value={newEvent.clientId || ''}
                  onChange={(e) => {
                    const client = clients.find(c => c.id === e.target.value);
                    setNewEvent({
                      ...newEvent,
                      clientId: e.target.value || null,
                      clientName: client ? client.name : ''
                    });
                  }}
                  className="modern-select"
                >
                  <option value="">No client linked</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="eventLocation">Location</label>
                <input
                  type="text"
                  id="eventLocation"
                  placeholder="e.g., Main Gym, Online, etc."
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  className="modern-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="eventDescription">Description</label>
                <textarea
                  id="eventDescription"
                  placeholder="Add event details..."
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  className="modern-textarea"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="eventNotes">Private Notes</label>
                <textarea
                  id="eventNotes"
                  placeholder="Add private notes (only visible to you)..."
                  value={newEvent.notes}
                  onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                  className="modern-textarea"
                  rows="2"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowEventModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCreateEvent}
                disabled={eventSaving || !newEvent.title}
              >
                {eventSaving ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
